import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Heart, Loader2, Lock, ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useDonate } from "@/context/DonateContext";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import EmbeddedPayment from "@/components/EmbeddedPayment";
import { QUOTE } from "@/config/content";

const PRESETS = [40, 80, 120, 250, 500];

export default function DonationDialog() {
  const { open, setOpen, initialAmount } = useDonate();
  const [phase, setPhase] = useState("form"); // form | pay | success
  const [frequency, setFrequency] = useState("one-time");
  const [amount, setAmount] = useState(80);
  const [custom, setCustom] = useState("");
  const [isOther, setIsOther] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const effectiveAmount = isOther ? parseFloat(custom) : amount;

  useEffect(() => {
    if (open) {
      setPhase("form");
      setClientSecret(null);
      setSessionId(null);
      setLoading(false);
      if (initialAmount && PRESETS.includes(initialAmount)) { setAmount(initialAmount); setIsOther(false); }
    }
  }, [open, initialAmount]);

  // Pause Lenis smooth-scroll while modal is open so the page behind cannot scroll.
  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => window.__lenis?.start();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!effectiveAmount || effectiveAmount < 5) {
      toast.error("Please choose a donation amount of at least $5.");
      return;
    }
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email so we can send your receipt.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "donation",
        amount: Math.round(effectiveAmount * 100) / 100,
        frequency,
        origin_url: window.location.origin,
        donor: { name, email, phone },
      });
      setClientSecret(data.client_secret);
      setSessionId(data.session_id);
      setPhase("pay");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setPhase("success");
    if (sessionId) api.get(`/payments/status/${sessionId}`).catch(() => {});
  };

  const freqBtn = (val, label) => (
    <button type="button" onClick={() => setFrequency(val)} data-testid={`donate-freq-${val}`} aria-pressed={frequency === val}
      className={`flex-1 rounded-sm border py-2.5 text-sm font-semibold transition-colors ${frequency === val ? "border-gold bg-gold text-ink" : "border-gold/30 text-ivory2 hover:border-gold/60"}`}>
      {label}
    </button>
  );

  const inputCls = "w-full rounded-sm border border-gold/30 bg-ink px-3.5 py-2.5 text-base text-ivory placeholder:text-ivory2/40 focus:border-gold";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-lenis-prevent
        className="w-[calc(100vw-1.5rem)] max-w-[440px] max-h-[92vh] overflow-y-auto overscroll-contain border border-gold/30 bg-ink2 text-ivory p-0 rounded-sm gap-0"
        data-testid="donation-dialog"
      >
        {phase === "success" ? (
          <div className="px-6 py-10 text-center" data-testid="donation-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <DialogTitle className="mt-5 font-serif text-3xl font-light text-ivory">Thank you.</DialogTitle>
            <DialogDescription className="mt-3 text-ivory2 text-[15px] leading-relaxed">
              Your {frequency === "monthly" ? "monthly " : ""}gift of ${Number(effectiveAmount).toFixed(2)} has been received.
              A receipt is on its way to {email}.
            </DialogDescription>
            <blockquote className="mt-6 border-l-2 border-gold pl-4 text-left font-serif text-lg italic text-ivory2">
              "{QUOTE.text}"<footer className="mt-1 not-italic text-sm text-gold">— {QUOTE.attribution}, {QUOTE.year}</footer>
            </blockquote>
            <button onClick={() => setOpen(false)} data-testid="donation-close-btn"
              className="mt-8 w-full rounded-sm bg-gold py-3.5 text-base font-bold uppercase tracking-wider text-ink hover:bg-goldhi">Close</button>
          </div>
        ) : phase === "pay" ? (
          <div>
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gold/20 bg-ink2 px-5 py-4">
              <button onClick={() => setPhase("form")} aria-label="Go back" data-testid="donate-back-btn"
                className="flex h-9 w-9 items-center justify-center rounded-sm border border-gold/30 text-gold hover:bg-gold/10">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <DialogTitle className="font-serif text-xl font-light text-ivory">Complete Your Donation</DialogTitle>
                <DialogDescription className="text-[13px] text-ivory2/70">${Number(effectiveAmount).toFixed(2)} {frequency === "monthly" ? "per month" : "one-time"}</DialogDescription>
              </div>
            </div>
            <EmbeddedPayment clientSecret={clientSecret} onComplete={handleComplete} />
          </div>
        ) : (
          <>
            <div className="sticky top-0 z-10 border-b border-gold/20 bg-ink2 px-5 py-4">
              <div className="kicker flex items-center gap-2 text-[11px]"><Heart className="h-3.5 w-3.5" /> Support a Scholar</div>
              <DialogTitle className="mt-1 font-serif text-2xl font-light text-ivory">Make a Donation</DialogTitle>
              <DialogDescription className="mt-0.5 text-ivory2/75 text-[13px] leading-snug">
                Your gift funds scholarships for university students across Ethiopia.
              </DialogDescription>
            </div>

            <form onSubmit={submit} className="px-5 py-5 space-y-5">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-ivory2/80">Frequency</label>
                <div className="flex gap-2.5">{freqBtn("one-time", "One-Time")}{freqBtn("monthly", "Monthly")}</div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-ivory2/80">Choose an amount</label>
                <div className="grid grid-cols-3 gap-2.5">
                  {PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => { setAmount(p); setIsOther(false); }} data-testid={`donate-amount-${p}`} aria-pressed={!isOther && amount === p}
                      className={`rounded-sm border py-3 font-serif text-xl transition-colors ${!isOther && amount === p ? "border-gold bg-gold text-ink" : "border-gold/30 text-ivory hover:border-gold/60"}`}>
                      ${p}
                    </button>
                  ))}
                  <button type="button" onClick={() => setIsOther(true)} data-testid="donate-amount-other" aria-pressed={isOther}
                    className={`rounded-sm border py-3 text-base font-semibold transition-colors ${isOther ? "border-gold bg-gold text-ink" : "border-gold/30 text-ivory hover:border-gold/60"}`}>
                    Other
                  </button>
                </div>
                {isOther && (
                  <div className="mt-2.5 flex items-center rounded-sm border border-gold/40 bg-ink px-3.5">
                    <span className="text-xl text-gold">$</span>
                    <input type="number" min="5" step="1" inputMode="decimal" autoFocus value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Enter amount" data-testid="donate-custom-input"
                      className="w-full bg-transparent py-2.5 pl-2 text-xl text-ivory placeholder:text-ivory2/40 focus:outline-none" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <p className="text-[13px] leading-snug text-ivory2/70">
                  Become a member and sustain a scholar: <span className="text-gold">$40</span> supports one student,
                  <span className="text-gold"> $80</span> two, and <span className="text-gold">$120</span> three —
                  or give any amount you wish.
                </p>
                <div>
                  <label htmlFor="d-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ivory2/80">Full name</label>
                  <input id="d-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="donate-name-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="d-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ivory2/80">Email (for your receipt)</label>
                  <input id="d-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="donate-email-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="d-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ivory2/80">Phone <span className="normal-case font-normal text-ivory2/50">(optional)</span></label>
                  <input id="d-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="donate-phone-input" className={inputCls} />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="donate-submit-btn"
                className="flex w-full items-center justify-center gap-2.5 rounded-sm bg-gold py-3.5 text-base font-bold uppercase tracking-wider text-ink transition-colors hover:bg-goldhi disabled:opacity-70">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Preparing…</> : <><Lock className="h-4 w-4" /> Continue to Payment</>}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-ivory2/60">
                <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Secure payment via Stripe · Tax-deductible 501(c)(3)
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
